import { Component } from '@theme/component';
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

// BFCM-PICKER-START (pure functions; scripts/check-bfcm.cjs evaluates this exact block)
/** Parse calendar rows once into epoch milliseconds; rows without explicit timezones are dropped. */
function parseWindows(rows) {
  const stamp = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:\d{2})$/;
  const windows = [];
  for (const row of Array.isArray(rows) ? rows : []) {
    if (!row || !stamp.test(row.start || '') || !stamp.test(row.end || '')) continue;
    const timerOk = row.timer && stamp.test(row.timer.target || '');
    windows.push({ ...row, startMs: Date.parse(row.start), endMs: Date.parse(row.end), timer: timerOk ? { ...row.timer, targetMs: Date.parse(row.timer.target) } : null });
  }
  return windows;
}

/** Half-open windows: live when start <= now < end. Returns null outside every window. */
function pickWindow(windows, nowMs) {
  for (const window of windows) if (window.startMs <= nowMs && nowMs < window.endMs) return window;
  return null;
}

/** Whole seconds left, rounded up, split into units; null at or after zero. */
function countdownParts(targetMs, nowMs) {
  const remaining = targetMs - nowMs;
  if (!Number.isFinite(remaining) || remaining <= 0) return null;
  const total = Math.ceil(remaining / 1000);
  return { days: Math.floor(total / 86400), hours: Math.floor(total / 3600) % 24, minutes: Math.floor(total / 60) % 60, seconds: total % 60 };
}

function formatCountdown(parts) {
  if (!parts) return '';
  const pad = (value) => String(value).padStart(2, '0');
  return `${parts.days}d ${pad(parts.hours)}:${pad(parts.minutes)}:${pad(parts.seconds)}`;
}
// BFCM-PICKER-END

/** Calendar rows embedded by snippets/evenmorn-bfcm-calendar.liquid (part: 'json'). */
function readCalendar(root) {
  try {
    return parseWindows(JSON.parse(root.querySelector('script[data-bfcm-calendar]')?.textContent || '[]'));
  } catch {
    return [];
  }
}

/** QA only: ?bfcm_now=2026-11-27T12:00:00-05:00 previews another moment client-side. */
function clockOffset() {
  const preview = new URLSearchParams(location.search).get('bfcm_now');
  const parsed = preview ? Date.parse(preview) : NaN;
  return Number.isFinite(parsed) ? parsed - Date.now() : 0;
}

/** Announcement bar: re-picks the live window every second because pages can be served from cache.
 * @extends {Component<{sale?: HTMLElement, banner?: HTMLElement, sub?: HTMLElement, normal?: HTMLElement, timer?: HTMLElement, label?: HTMLElement, days?: HTMLElement, hours?: HTMLElement, minutes?: HTMLElement, seconds?: HTMLElement}>}
 */
class EvenmornBfcmBar extends Component {
  #timer;
  #windows = [];
  #offset = 0;
  #current;

  connectedCallback() {
    super.connectedCallback();
    this.#initialize();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#initialize();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearInterval(this.#timer);
  }

  #initialize() {
    clearInterval(this.#timer);
    this.#windows = readCalendar(this);
    this.#offset = clockOffset();
    this.#current = undefined;
    this.#update();
    this.#timer = setInterval(this.#update, 1000);
  }

  #update = () => {
    const now = Date.now() + this.#offset;
    const live = pickWindow(this.#windows, now);
    const id = live ? live.id : '';
    if (id !== this.#current) {
      this.#current = id;
      this.#swap(live);
    }
    const { timer, label, days, hours, minutes, seconds } = this.refs;
    if (!timer) return;
    const parts = live && live.timer ? countdownParts(live.timer.targetMs, now) : null;
    timer.hidden = !parts;
    if (!parts) return;
    if (label) label.textContent = live.timer.label || '';
    if (days) days.textContent = String(parts.days);
    if (hours) hours.textContent = String(parts.hours).padStart(2, '0');
    if (minutes) minutes.textContent = String(parts.minutes).padStart(2, '0');
    if (seconds) seconds.textContent = String(parts.seconds).padStart(2, '0');
  };

  /** Swap bar copy and every server-rendered BFCM badge or tier line at a window boundary. */
  #swap(live) {
    const { sale, banner, sub, normal } = this.refs;
    if (banner) banner.textContent = live ? live.banner : '';
    if (sub) sub.textContent = live ? live.banner_sub : '';
    if (sale) sale.hidden = !live;
    if (normal) normal.hidden = Boolean(live);
    for (const badge of document.querySelectorAll('[data-bfcm-badge]')) {
      badge.textContent = live ? live.badge : badge.dataset.defaultText || '';
      badge.hidden = !badge.textContent;
    }
    for (const line of document.querySelectorAll('[data-bfcm-tier-line]')) line.hidden = !live || (live.tier !== 'tier2' && live.tier !== 'tier3');
  }
}

/** Optional: after a cart add, put 1 one-time single of the same product next to its 5-pack, once per cart,
 * during tier2/tier3 windows. A Shopify Buy X get Y automatic discount prices it at $0.
 */
class EvenmornBfcmGift extends Component {
  #listeners;
  #busy = false;

  connectedCallback() {
    super.connectedCallback();
    this.#listeners?.abort();
    this.#listeners = new AbortController();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#cartChanged, { signal: this.#listeners.signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners?.abort();
  }

  #cartChanged = (event) => {
    if (this.#busy || event.action !== 'add' || event.target === this) return;
    event.promise?.then((result) => {
      if (!result?.detail?.didError) this.#maybeAdd();
    }, () => {});
  };

  async #maybeAdd() {
    const trigger = Number(this.dataset.trigger);
    const gift = Number(this.dataset.gift);
    const live = pickWindow(readCalendar(this), Date.now() + clockOffset());
    if (!trigger || !gift || !live || (live.tier !== 'tier2' && live.tier !== 'tier3')) return;
    this.#busy = true;
    try {
      const cartUrl = new URL(this.dataset.cartUrl, location.origin);
      const before = await (await fetch(cartUrl)).json();
      const items = Array.isArray(before.items) ? before.items : [];
      const key = `evenmorn-bfcm-gift:${before.token || ''}:${gift}`;
      const hasTrigger = items.some((item) => item.variant_id === trigger);
      const hasGift = items.some((item) => item.variant_id === gift && !item.selling_plan_allocation);
      if (!hasTrigger || hasGift || sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, '1');
      const sections = [...new Set([...document.querySelectorAll('cart-items-component')].map((node) => node.dataset.sectionId).filter(Boolean))];
      const deferred = CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(new CartLinesUpdateEvent({ action: 'add', context: 'product', lines: [{ merchandiseId: String(gift), quantity: 1 }], promise: deferred.promise }));
      try {
        const response = await fetch(new URL(this.dataset.addUrl, location.origin), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: gift, quantity: 1 }], sections: sections.join(','), sections_url: location.pathname }),
        });
        const added = await response.json();
        if (!response.ok || added.status || added.errors) throw new Error(String(added.description || 'add failed'));
        const cart = await (await fetch(cartUrl)).json();
        deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { sections: added.sections, items: cart.items, didError: false, source: 'evenmorn-bfcm-gift' } });
      } catch (error) {
        sessionStorage.removeItem(key);
        deferred.resolve({ cart: null, detail: { didError: true, source: 'evenmorn-bfcm-gift' } });
      }
    } catch {
      // Cart read failed: leave the cart exactly as the shopper made it.
    } finally {
      this.#busy = false;
    }
  }
}

for (const [name, element] of [['evenmorn-bfcm-bar', EvenmornBfcmBar], ['evenmorn-bfcm-gift', EvenmornBfcmGift]]) {
  if (!customElements.get(name)) customElements.define(name, element);
}
